import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarCheck, Layers3, Ticket, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtOdds, pct, SPORT_META } from "@/lib/odds";
import { ParlaySlip } from "@/components/ParlaySlip";
import { Ladder } from "@/pages/Props";
import { StreakBadge } from "@/pages/Players";
import { MoveChip } from "@/components/MoveChip";
import { FollowButton } from "@/components/FollowButton";
import { StreakActions } from "@/components/StreakActions";
import { ShareCardModal } from "@/components/PlayerShareCard";
import { useSlip } from "@/context/SlipContext";

function Stat({ label, value, color = "text-white", id }) {
  return (
    <div className="rounded-lg border border-[#26334D] bg-[#121824] px-4 py-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p data-testid={id} className={`font-mono-num mt-1 text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function CallRow({ c }) {
  const won = c.result === "won";
  return (
    <li data-testid="player-call" className="flex items-center gap-3 px-4 py-2.5 text-sm">
      <span className={`h-7 w-7 shrink-0 rounded-full text-center font-mono-num text-xs font-black leading-7 ${c.result === "pending" ? "bg-slate-700 text-slate-300" : won ? "bg-emerald-500 text-[#0B0E14]" : "bg-red-500/80 text-white"}`}>{c.result === "pending" ? "·" : won ? "W" : "L"}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-white">{c.selection.replace(/^.*?\(\w+\) /, "")}{c.market === "Alt Line" && <span className="ml-1.5 rounded bg-fuchsia-500/20 px-1 text-[9px] font-bold text-fuchsia-300">ALT</span>}</p>
        <p className="truncate text-xs text-slate-500">{new Date(c.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {c.matchup}</p>
      </div>
      {c.actual != null && <span className="font-mono-num text-xs text-slate-400">actual <span className={`font-bold ${won ? "text-emerald-300" : "text-red-300"}`} data-testid="player-call-actual">{c.actual}</span></span>}
      <span className="font-mono-num w-12 text-right text-amber-400">{fmtOdds(c.odds)}</span>
    </li>
  );
}

export default function Player() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const { replace } = useSlip();
  useEffect(() => {
    setD(null);
    api.get(`/players/${slug}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e, "Player not found")));
  }, [slug]);

  if (err) return <main className="mx-auto max-w-4xl px-4 py-16 text-center" data-testid="player-not-found"><p className="text-slate-400">{err}</p><Link to="/players" className="btn-ghost mt-6 inline-flex">Back to players</Link></main>;
  if (!d) return <main className="mx-auto max-w-7xl px-4 py-10"><div className="glass-card h-64 animate-pulse" data-testid="player-loading" /></main>;
  const { player, today, record: r } = d;
  const meta = SPORT_META[player.sport];
  const load = (s) => {
    replace(s.legs, { title: s.title, strategy: s.reasoning });
    toast.success(`"${s.title}" loaded into your slip`);
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="player-page">
      <Link to="/players" data-testid="player-back-link" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400 transition-colors hover:text-emerald-400"><ArrowLeft size={13} /> All players</Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="label-caps" style={{ color: meta?.color }}>{meta?.short === player.league ? player.league : `${meta?.short} · ${player.league}`}</span>
            <span className="rounded bg-[#0B0E14] px-1.5 py-0.5 font-mono-num text-[10px] text-slate-400">{player.team}</span>
          </div>
          <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl" data-testid="player-name">{player.name}</h1>
          <p className="mt-2 text-sm text-slate-400" data-testid="player-today-line">
            {today ? <span className="flex items-center gap-1.5 text-emerald-300"><CalendarCheck size={14} /> Playing today · {today.matchup} · {new Date(today.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span> : "Not on today's slate — history below."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2"><FollowButton slug={player.slug} name={player.name} size="lg" /><ShareCardModal d={d} /><StreakBadge record={r} size="lg" />{r.streak && !r.hot && !r.cold && <span className="rounded-full border border-[#26334D] px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-300" data-testid="player-streak">Last {r.streak.n} call{r.streak.n > 1 ? "s" : ""}: {r.streak.result === "won" ? "hit" : "missed"}</span>}</div>
      </div>
      {today && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-[#26334D] bg-[#121824]/70 px-4 py-3" data-testid="streak-parlay-bar">
          <p className="text-sm text-slate-300"><span className="font-bold text-white">One-tap streak parlay:</span> {r.hot ? `${player.name.split(" ")[0]} has hit ${r.hot.streak} straight ${r.hot.stat} calls.` : r.cold ? `${player.name.split(" ")[0]} has missed ${r.cold.streak} straight ${r.cold.stat} calls.` : "Ride the model's call or fade it — paired with today's two strongest straight plays."}</p>
          <span className="ml-auto"><StreakActions slug={player.slug} record={r} playingToday size="lg" /></span>
        </div>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-4" data-testid="player-record">
        <Stat label={`Model hit rate · ${d.window_days}d`} value={r.hit_rate != null ? `${r.hit_rate}%` : "—"} color={r.hit_rate >= 55 ? "text-emerald-400" : r.hit_rate < 45 ? "text-red-400" : "text-amber-400"} id="player-hit-rate" />
        <Stat label="Record on props" value={`${r.won}–${r.lost}`} id="player-wl" />
        <Stat label="Games tracked" value={d.games_tracked} id="player-games" />
        <Stat label="Props today" value={d.ladders.length} color="text-cyan-300" id="player-props-today" />
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <section className="min-w-0 space-y-8">
          {d.ladders.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Today's props & alt lines</h2>
              <p className="mt-1 text-xs text-slate-400">Every rung with live movement since the morning open. Tap any line to add it to your slip.</p>
              <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="player-ladders">{d.ladders.map((g) => <Ladder key={g.key} g={g} showHeader={false} />)}</div>
            </div>
          )}

          {d.calls_today.length > 0 && (
            <div data-testid="player-calls-today">
              <h2 className="font-display flex items-center gap-2 text-2xl font-bold uppercase tracking-tight text-white"><Sparkles size={18} className="text-cyan-400" /> The model's calls today</h2>
              <ul className="mt-3 divide-y divide-[#26334D]/60 rounded-lg border border-[#26334D] bg-[#0B0E14]/60">
                {d.calls_today.map((c) => (
                  <li key={c.pick_id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm" data-testid="player-call-today">
                    <span className="min-w-0 flex-1 truncate font-semibold text-white">{c.selection.replace(/^.*?\(\w+\) /, "")}</span>
                    <MoveChip movement={c.movement} />
                    <span className="font-mono-num text-amber-400">{fmtOdds(c.odds)}</span>
                    <span className="font-mono-num text-xs text-emerald-400">{pct(c.win_probability)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">By stat</h2>
              {r.by_stat.length === 0 ? <p className="mt-3 text-sm text-slate-500" data-testid="player-by-stat-empty">The model hasn't settled a prop call on this player yet.</p> : (
                <ul className="mt-3 space-y-2" data-testid="player-by-stat">
                  {r.by_stat.map((s) => (
                    <li key={s.stat} data-testid="player-stat-row" className="glass-card flex items-center gap-3 px-4 py-2.5">
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{s.stat}</span>
                      <span className="font-mono-num text-xs text-slate-400">{s.won}–{s.n - s.won}</span>
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-[#0B0E14]"><div className={`h-full ${s.hit_rate >= 55 ? "bg-emerald-400" : s.hit_rate < 45 ? "bg-red-400" : "bg-amber-400"}`} style={{ width: `${s.hit_rate}%` }} /></div>
                      <span className={`font-mono-num w-10 text-right text-sm font-bold ${s.hit_rate >= 55 ? "text-emerald-400" : s.hit_rate < 45 ? "text-red-400" : "text-amber-400"}`}>{s.hit_rate}%</span>
                      {s.streak >= 3 && <span className={`rounded px-1.5 py-0.5 font-mono-num text-[10px] font-bold ${s.streak_result === "won" ? "bg-orange-500/15 text-orange-300" : "bg-sky-500/15 text-sky-300"}`}>{s.streak}{s.streak_result === "won" ? "W" : "L"} run</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Last {Math.min(r.last_calls.length, 12)} calls</h2>
              <ul className="mt-3 divide-y divide-[#26334D]/60 rounded-lg border border-[#26334D] bg-[#0B0E14]/60" data-testid="player-last-calls">
                {r.last_calls.map((c) => <CallRow key={c.pick_id} c={c} />)}
                {r.last_calls.length === 0 && <li className="px-4 py-6 text-center text-sm text-slate-500">No settled calls yet.</li>}
              </ul>
            </div>
          </div>

          {d.sgps_today.length > 0 && (
            <div data-testid="player-sgps">
              <h2 className="font-display flex items-center gap-2 text-2xl font-bold uppercase tracking-tight text-white"><Layers3 size={18} className="text-cyan-400" /> Same game parlays featuring {player.name.split(" ")[0]}</h2>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {d.sgps_today.map((s) => (
                  <div key={s.sgp_id} className="glass-card p-4" data-testid="player-sgp-card">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0"><p className="truncate text-xs text-slate-500">{s.matchup}</p><h3 className="font-display text-xl font-bold leading-none text-white">{s.title}</h3></div>
                      <p className="font-mono-num text-xl font-bold text-amber-400">{fmtOdds(s.combined_american)}</p>
                    </div>
                    <ul className="mt-3 space-y-1 text-xs text-slate-300">{s.legs.map((l) => <li key={l.pick_id} className={`truncate ${l.player?.slug === slug ? "font-bold text-emerald-300" : ""}`}>· {l.selection} <span className="font-mono-num text-amber-400">{fmtOdds(l.odds)}</span></li>)}</ul>
                    <div className="mt-3 flex items-center justify-between"><span className="text-xs text-slate-500">Hit rate <span className="font-mono-num font-bold text-emerald-400">{pct(s.win_probability)}</span></span><button data-testid="player-sgp-load" onClick={() => load(s)} className="btn-ghost !px-3 !py-1 text-xs"><Ticket size={13} /> Load</button></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
        <div className="hidden lg:sticky lg:top-24 lg:block lg:self-start"><ParlaySlip /></div>
      </div>
    </main>
  );
}
